import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

/**
 * ProtectedRoute - Wrapper component for role-based route protection
 * Checks authentication and optionally validates user role
 *
 * @param {React.ReactNode} children - Component to render if authorized
 * @param {string|string[]} allowedRoles - Role(s) allowed to access this route
 * @param {string} redirectTo - Path to redirect if unauthorized (default: role selection)
 */
const ProtectedRoute = ({ children, allowedRoles = null, redirectTo = '/role-selection' }) => {
  const { isAuthenticated, currentUser, loading } = useAuth();

  // Show loading spinner while checking authentication
  if (loading) {
    return (
      <div className="h-screen w-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="flex items-center justify-center space-x-2 p-2">
            <div className="h-3 w-3 bg-indigo-600 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
            <div className="h-3 w-3 bg-indigo-600 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
            <div className="h-3 w-3 bg-indigo-600 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
          </div>
          <p className="text-gray-600 mt-3 font-medium">Loading...</p>
        </div>
      </div>
    );
  }

  // Redirect to role selection if not authenticated
  if (!isAuthenticated) {
    return <Navigate to={redirectTo} replace />;
  }

  // If specific roles are required, check user's role
  if (allowedRoles) {
    const roles = Array.isArray(allowedRoles) ? allowedRoles : [allowedRoles];
    const userRole = currentUser?.user_type;

    if (!userRole || !roles.includes(userRole)) {
      // Redirect to appropriate dashboard based on user's actual role
      const dashboardMap = {
        'student': '/student/dashboard',
        'teacher': '/teacher/dashboard',
        'school-admin': '/school-admin/dashboard',
        'super-admin': '/super-admin/dashboard'
      };

      const userDashboard = dashboardMap[userRole] || '/role-selection';
      return <Navigate to={userDashboard} replace />;
    }
  }

  // User is authenticated and authorized
  return children;
};

export default ProtectedRoute;
